import React, { ComponentPropsWithRef, forwardRef } from "react";
import styled from "styled-components";

import { ACTIVITY_VARIANTS } from "@adsk/acc-reference-picker/es/plugins/constants";
import { ActivityCard } from "@adsk/alloy-react-activity-log";
import Badge from "@adsk/alloy-react-badge";
import { useLocale } from "@adsk/alloy-react-locale";
import theme from "@adsk/alloy-react-theme";
import { OverflowTooltip } from "@adsk/alloy-react-tooltip";

import { SystemWithCategoryAndStatus } from "../../models";
import InlineStatusColorIndicator from "../shared/InlineStatusColorIndicator";

const Details = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-evenly;
  overflow: hidden;
`;

const Name = styled.div`
  ${theme.typography.heading4}
`;

const Status = styled.div({
  ...theme.typography.labelSmall,
  display: "inline-block",
  verticalAlign: "middle",
  marginTop: "4px",
});

type SystemsActivityCardProps = ComponentPropsWithRef<typeof ActivityCard> & {
  variant?: typeof ACTIVITY_VARIANTS.ADDED | typeof ACTIVITY_VARIANTS.REMOVED;
  entity: SystemWithCategoryAndStatus;
};

const StyledBadge = styled(Badge)`
  margin-left: 8px;
`;

const SystemsActivityCard = forwardRef<
  HTMLDivElement,
  SystemsActivityCardProps
>(({ entity: system, variant, ...rest }, ref) => {
  // eslint-disable-next-line @typescript-eslint/no-require-imports
  const { T } = useLocale(({ locale }) => require(`../i18n/${locale}.json`));

  return (
    <ActivityCard
      {...rest}
      ref={ref}
      title={
        variant === ACTIVITY_VARIANTS.REMOVED
          ? T.translate("ASSET_ACTIVITY_TITLE_REMOVED")
          : T.translate("ASSET_ACTIVITY_TITLE_ADDED")
      }
    >
      <Details>
        <Name>
          <OverflowTooltip content={system.name}>
            {system.name}
            <StyledBadge
              size={Badge.SIZES.SMALL}
              variant={Badge.VARIANTS.OUTLINE}
            >
              {T.translate("SYSTEM")}
            </StyledBadge>
          </OverflowTooltip>
        </Name>
        {system.status && (
          <Status>
            <OverflowTooltip>
              <InlineStatusColorIndicator color={system.status.color} />
              {system.status.label}
            </OverflowTooltip>
          </Status>
        )}
      </Details>
    </ActivityCard>
  );
});

export default SystemsActivityCard;
